<template>
    <div>
        <div v-if="tags && tags.length > 0" class="badge-box">
            <SbBadge
                v-for="(tag, tagIdx) in tags"
                :key="tagIdx"
                pill
                variant="secondary"
                class="mr-1"
                @click.prevent="removeTag(tagIdx)"
                role="button"
            >
                {{ tag }} <i class="fa-regular fa-times"></i>

                <input
                    v-if="inputName"
                    type="hidden"
                    :name="`${inputName}[]`"
                    :value="tag"
                />
            </SbBadge>
        </div>

        <hr />
        <div class="d-flex mt-3">
            <input
                placeholder="Lägg till"
                type="text"
                class="form-control form-control-sm flex-grow mr-2"
                v-model="newTag"
                @keydown.enter.prevent="addNewTag"
            />
            <SbButton
                type="button"
                variant="primary"
                size="sm"
                :disabled="newTag.length === 0"
                @click.prevent="addNewTag"
            >
                <i class="fa-regular fa-plus"></i>
            </SbButton>
        </div>

        <div
            v-if="autocompleteTags && autocompleteTags.length > 0"
            class="list-unstyled mt-3 badge-box d-flex justify-content-start flex-wrap"
        >
            <div
                v-for="(tag, tagIdx) in autocompleteTags"
                class="p-1 d-flex align-items-center"
            >
                <SbBadge
                    role="button"
                    pill
                    variant="company-color"
                    @click.prevent="addTag(tag)"
                >
                    {{ tag }} <i class="fa-regular fa-plus"></i>
                </SbBadge>
                <i
                    class="fa-regular fa-times"
                    role="button"
                    style="margin-left: 2px"
                    @click.prevent="deleteTag(tag)"
                ></i>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.badge-box {
    font-size: 1.1em;
}
</style>

<script setup>
import { route } from "ziggy";
import { computed, onMounted, ref, watch } from "vue";
import axios from "axios";
const emit = defineEmits(["update:modelValue"]);
const props = defineProps({
    quoteProfileId: {
        type: Number,
        required: true,
    },
    modelValue: {
        type: Array,
        default: undefined,
    },
    quoteId: {
        type: Number,
        default: undefined,
    },
    inputName: {
        type: String,
        default: undefined,
    },
    isTemplate: {
        type: Boolean,
        default: false,
    },
});
const tags = ref(
    JSON.parse(JSON.stringify(props.modelValue ?? []))
);
const availableTags = ref([]);
const newTag = ref("");
const autocompleteTags = computed(() => {
    if (!availableTags.value || !tags.value) {
        return [];
    }
    const potentialTags = availableTags.value
        .map((tag) => tag.title)
        .filter((tag) => !tags.value.includes(tag))
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    if (!newTag.value) {
        return potentialTags.length <= 50 ? potentialTags : [];
    }
    return potentialTags.filter((tag) =>
        tag.toLowerCase().includes(newTag.value.toLowerCase())
    );
});
function captureValue(val) {
    if (props.quoteId !== undefined) {
        return;
    }
    if (JSON.stringify(tags.value) !== JSON.stringify(val)) {
        tags.value = JSON.parse(JSON.stringify(val));
    }
}
function addNewTag() {
    addTag(newTag.value);
}
function addTag(tag) {
    const foundInQuoteTags = tags.value.find((item) => item === tag);
    if (foundInQuoteTags) {
        newTag.value = "";
        return;
    }
    tags.value.push(tag);
    availableTags.value = availableTags.value.map((item) => {
        if (item.title !== tag) {
            return item;
        }
        item.quotes_count++;
        return item;
    });
    newTag.value = "";
}
function removeTag(idx) {
    const removedTags = tags.value.splice(idx, 1);
    if (removedTags.length < 1) {
        return;
    }
    availableTags.value = availableTags.value.map((item) => {
        if (item.title !== removedTags[0]) {
            return item;
        }
        item.quotes_count--;
        return item;
    });
    const hasQuotes = availableTags.value.find(
        (item) => item.title === removedTags[0]
    );
    if (hasQuotes) {
        return;
    }
    availableTags.value.push({
        id: undefined,
        title: removedTags[0],
        quotes_count: 0,
    });
}
async function deleteTag(tag) {
    const count =
        availableTags.value.find((item) => item.title === tag)?.quotes_count ??
        0;
    if (
        count > 0 &&
        !confirm(
            `Den här taggen har ${count} offert${
                count === 1 ? "" : "er"
            }. Är du säker på att du vill ta bort den?`
        )
    ) {
        return;
    }
    await axios.delete(
        route("quotes.deleteTags", {
            id: props.quoteProfileId,
            is_template: props.isTemplate,
        }),
        {
            data: {
                tags: [tag],
            },
            headers: {
                "Content-Type": "application/json",
            },
        }
    );
    availableTags.value = availableTags.value.filter(
        (item) => item.title !== tag
    );
}
watch(
    () => props.modelValue,
    () => captureValue(props.modelValue ?? []),
    { immediate: true }
);
watch(
    tags,
    async (val) => {
        if (props.quoteId === undefined) {
            const nextValue = JSON.parse(JSON.stringify(val));
            emit("update:modelValue", nextValue);
            return;
        }
        await axios.post(
            route("quotes.updateTags", {
                id: props.quoteId,
                is_template: props.isTemplate,
            }),
            {
                tags: val,
            }
        );
    },
    {
        deep: true,
    }
);
onMounted(async () => {
    const response = await axios.get(
        route("quoteProfiles.tags", props.quoteProfileId)
    );
    availableTags.value = response.data.data;
});
</script>
